import { FunctionComponent, ReactNode, useState } from "react";
import { Layout, Drawer } from "antd";
import { useMediaQuery } from "react-responsive";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/AuthContext";
import { getNavGroups, getPageTitle } from "./navigationConfig";
import { SidebarMenu } from "./SidebarMenu";
import { TopBar } from "./TopBar";
import { AccountFooter } from "./AccountFooter";

const { Sider, Header, Content } = Layout;
const COLLAPSE_KEY = "amc.sider.collapsed";

interface AppShellProps {
    children: ReactNode;
}

export const AppShell: FunctionComponent<AppShellProps> = ({ children }) => {
    const { roles } = useAuth();
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const [collapsed, setCollapsed] = useState<boolean>(() => localStorage.getItem(COLLAPSE_KEY) === "true");
    const [drawerOpen, setDrawerOpen] = useState(false);
    const location = useLocation();

    const groups = getNavGroups(roles ?? []);
    const title = getPageTitle(location.pathname);

    const toggleCollapsed = () =>
        setCollapsed((current) => {
            const next = !current;
            localStorage.setItem(COLLAPSE_KEY, String(next));
            return next;
        });

    const brand = (
        <div className="app-sider-brand">
            <span className="logo" />
            {!collapsed && <span className="app-sider-title">AMC</span>}
        </div>
    );

    return (
        <Layout className="app-shell" style={{ minHeight: "100vh" }}>
            {!isMobile && (
                <Sider theme="dark" collapsible collapsed={collapsed} trigger={null} width={230} className="app-sider">
                    {brand}
                    <div className="app-nav-scroll">
                        <SidebarMenu groups={groups} onNavigate={() => setCollapsed(true)} />
                    </div>
                    <AccountFooter collapsed={collapsed} onNavigate={() => setCollapsed(true)} />
                </Sider>
            )}
            {isMobile && (
                <Drawer
                    placement="left"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    closable={false}
                    width={230}
                    styles={{ body: { padding: 0, background: "#001529", display: "flex", flexDirection: "column" } }}
                >
                    <div className="app-sider-brand"><span className="logo" /><span className="app-sider-title">AMC</span></div>
                    <div className="app-nav-scroll">
                        <SidebarMenu groups={groups} onNavigate={() => setDrawerOpen(false)} />
                    </div>
                    <AccountFooter onNavigate={() => setDrawerOpen(false)} />
                </Drawer>
            )}
            <Layout>
                <Header className="app-header-wrap" style={{ background: "#fff", padding: 0 }}>
                    <TopBar
                        title={title}
                        isMobile={isMobile}
                        collapsed={isMobile ? false : collapsed}
                        onToggle={() => (isMobile ? setDrawerOpen(true) : toggleCollapsed())}
                    />
                </Header>
                <Content className="app-content">{children}</Content>
            </Layout>
        </Layout>
    );
};
